import {HttpClient, HttpParams} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {FileUtils} from '@app/core/utils/file.utils';
import {Observable, map} from 'rxjs';
import {environment} from 'src/environments/environment';
import {CheckoutStep} from '../enums/checkout-step.enum';
import {ICustomerOrganization} from '../models/customer-organization.model';
import {Order} from '../models/order-request.model';
import {OrderResponse} from '../models/order-response.model';
import {ICheckoutStep} from '../models/order-step.model';
import {IPatientRelation} from '../models/patient-relation.model';
import {ISampleType} from '../models/sample-type.model';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private readonly _httpClient = inject(HttpClient);

  contentType = 'application/pdf';
  fileName = '';
  private readonly createOrderUrl = `${environment.api.baseUrl}/${environment.api.checkout.createOrder}`;
  private readonly updateOrderUrl = `${environment.api.baseUrl}/${environment.api.checkout.updateOrder}`;
  private readonly cancelOrderUrl = `${environment.api.baseUrl}/${environment.api.checkout.cancelOrder}`;
  private readonly updateOrderStatusUrl = `${environment.api.baseUrl}/${environment.api.checkout.updateOrderStatus}`;
  private readonly getOrderUrl = `${environment.api.baseUrl}/${environment.api.checkout.getOrder}`;
  private readonly getCustomerOrganizationUrl = `${environment.api.baseUrl}/${environment.api.user.getCustomerOrganization}`;
  private readonly getConsentPdfUrl = `${environment.api.baseUrl}/${environment.api.checkout.getConsentPdf}`;
  private readonly getSampleTypesUrl = `${environment.api.baseUrl}/${environment.api.checkout.getSampleTypes}`;
  private readonly getRelatedPatientTypesUrl = `${environment.api.baseUrl}/${environment.api.config.getPatientRelationType}`;

  getOrder(id: number): Observable<OrderResponse> {
    return this._httpClient.get<OrderResponse>(`${this.getOrderUrl}/${id}`);
  }

  createOrder(orderData: Order): Observable<Order> {
    return this._httpClient.post<Order>(this.createOrderUrl, orderData);
  }

  updateOrder(orderData: Order): Observable<OrderResponse> {
    return this._httpClient.put<OrderResponse>(this.updateOrderUrl, orderData);
  }

  updateOrderStatus(orderId: number, statusId: number): Observable<Order> {
    const body = null;

    let params = new HttpParams();
    params = params.append('orderId', orderId);
    params = params.append('statusId', statusId);

    return this._httpClient.put<Order>(this.updateOrderStatusUrl, body, {
      params: params,
    });
  }

  getCustomerOrganization(customerId: number): Observable<ICustomerOrganization[]> {
    return this._httpClient.get<ICustomerOrganization[]>(`${this.getCustomerOrganizationUrl}/${customerId}`);
  }
  getCheckoutStepsStatus(lastCompletedStep: number | null, currentStep: number) {
    const steps: ICheckoutStep[] = [];
    if (lastCompletedStep != null) {
      for (let i = CheckoutStep.OrderInfo; i <= lastCompletedStep && i <= CheckoutStep.ConfirmOrder; i++) {
        steps.push({stepId: i, enabled: true});
      }
      for (let i = lastCompletedStep + 1; i <= CheckoutStep.ConfirmOrder; i++) {
        steps.push({stepId: i, enabled: false});
      }
    } else {
      for (let i = CheckoutStep.OrderInfo; i <= CheckoutStep.ConfirmOrder; i++) {
        steps.push({stepId: i, enabled: false});
      }
    }
    return {
      steps,
      lastCompletedStep: lastCompletedStep,
      currentStep: currentStep,
    };
  }

  getConsentPdf(orderId: number, productId: number, languageId: number): Observable<ArrayBuffer | null> {
    const getConsentPdfUrl = `${this.getConsentPdfUrl}/${orderId}/${productId}/${languageId}`;
    return this._httpClient
      .get(getConsentPdfUrl, {
        observe: 'response',
        responseType: 'arraybuffer',
      })
      .pipe(
        map(response => {
          try {
            this.contentType = FileUtils.getContentType(response.headers) || this.contentType;
            this.fileName = FileUtils.getContentDisposition(response.headers);
          } catch (error) {
            console.log(`Can't extract filename from response headers.`, error);
          }
          return response.body;
        })
      );
  }

  cancelOrder(id: number): Observable<boolean> {
    return this._httpClient.delete<boolean>(`${this.cancelOrderUrl}/${id}`);
  }

  getSampleTypes(productId?: number): Observable<ISampleType[]> {
    if (productId) {
      return this._httpClient.get<ISampleType[]>(`${this.getSampleTypesUrl}?productId=${productId}`);
    } else {
      return this._httpClient.get<ISampleType[]>(`${this.getSampleTypesUrl}`);
    }
  }

  getPatientRelationTypes(enabled: boolean, languageId: string): Observable<IPatientRelation[]> {
    return this._httpClient.get<IPatientRelation[]>(
      `${this.getRelatedPatientTypesUrl}?enabled=${enabled}&langId=${languageId}`
    );
  }
}
